import { useEffect, useMemo, useState } from "react";
import { FaHeartbeat, FaPlus, FaWeight } from "react-icons/fa";
import { GiBrain, GiDroplets, GiLungs } from "react-icons/gi";
import { MdOutlineHeight, MdSick } from "react-icons/md";
import { IoWaterOutline, IoTrendingUp, IoClose } from "react-icons/io5";
import { TbTemperature, TbScale } from "react-icons/tb";
import dayjs from "dayjs";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import VitalService from "../../../../Services/VitalService";
import VitalTrendChart from "./VitalTrendChart";
import AddIPDVitals from "./AddIPDVitals";

dayjs.extend(isSameOrBefore);

const VITAL_CARDS = [
  {
    code: "BP",
    title: "BP",
    unit: "mmHg",
    icon: <FaHeartbeat className="text-red-500 text-md" />,
    isBP: true,
    trendKey: "BP",
    color: "#FF9E9E",
  },
  {
    code: "HR",
    title: "Pulse",
    unit: "bpm",
    icon: <FaHeartbeat className="text-red-500 text-md" />,
    trendKey: "HR",
    color: "#FFD3B4",
  },
  {
    code: "RR",
    title: "RR",
    unit: "/min",
    icon: <GiLungs className="text-violet-500 text-md" />,
    trendKey: "RR",
    color: "#9DCBFF",
  },
  {
    code: "SPO2",
    title: "SpO₂",
    unit: "%",
    icon: <IoWaterOutline className="text-blue-500 text-md" />,
    trendKey: "SPO2",
    color: "#A8ECFF",
  },
  {
    code: "TP",
    title: "Temperature",
    unit: "°F",
    icon: <TbTemperature className="text-orange-500 text-md" />,
    trendKey: "TP",
    color: "#24CC8C",
  },
  {
    code: "WE",
    title: "Weight",
    unit: "kg",
    icon: <FaWeight className="text-green-500 text-md" />,
    trendKey: "WE",
    color: "#FFC0D7",
  },
  {
    code: "HI",
    title: "Height",
    unit: "cm",
    icon: <MdOutlineHeight className="text-blue-500 text-md" />,
    trendKey: null,
    color: "#DDC0FF",
  },
  {
    code: "BMI",
    title: "BMI",
    unit: "kg/m²",
    icon: <TbScale className="text-purple-500 text-md" />,
    trendKey: "BMI",
    color: "#FFEDC2",
  },
  {
    code: "SL",
    title: "Sugar Level",
    unit: "mg/dl",
    icon: <GiDroplets className="text-purple-500 text-md" />,
    trendKey: "SL",
    color: "#FFEDC2",
  },
  {
    code: "PS",
    title: "Pain Score",
    unit: "Score",
    icon: <MdSick className="text-purple-500 text-md" />,
    trendKey: "PS",
    color: "#FFEDC2",
  },
  {
    code: "CC",
    title: "Consciousness",
    unit: "Score",
    icon: <GiBrain className="text-purple-500 text-md" />,
    trendKey: "CC",
    color: "#FFEDC2",
  },
];

function groupHistoryByTimestamp(rows) {
  const grouped = {};
  rows.forEach((row) => {
    const ts = row.recordedDateTime;
    if (!grouped[ts]) grouped[ts] = { recordedDateTime: ts };
    grouped[ts][row.vitalCode] = row.value;
  });
  return Object.values(grouped).sort(
    (a, b) => new Date(b.recordedDateTime) - new Date(a.recordedDateTime),
  );
}

function formatDateTime(ts) {
  if (!ts) return "--";
  const d = new Date(ts.replace(" ", "T"));
  if (isNaN(d.getTime())) return ts;
  return d.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatVal(val) {
  if (val == null || val === 0 || val === "0" || val === "") return "--";
  return val;
}

export default function Vitals({ patientId, accountId, clinicId, admissionId }) {
  const [latestVitals, setLatestVitals] = useState([]);
  const [loadingLatest, setLoadingLatest] = useState(false);

  const [historyRows, setHistoryRows] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [historyError, setHistoryError] = useState(null);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  const [trendCard, setTrendCard] = useState(null);
  const [isAddVitalModalOpen, setIsAddVitalModalOpen] = useState(false);

  const fetchLatestVitals = async () => {
    if (!patientId) return;
    setLoadingLatest(true);
    try {
      const res = await VitalService.getPatientRecentVital({
        patientId,
        status: 1,
      });
      setLatestVitals(res?.data || []);
    } catch (e) {
      console.error("Failed to fetch latest vitals:", e);
      setLatestVitals([]);
    } finally {
      setLoadingLatest(false);
    }
  };

  const fetchHistory = async (from, to) => {
    if (!patientId) return;
    setLoadingHistory(true);
    setHistoryError(null);
    try {
      const payload = {
        account_Id: accountId,
        clinic_Id: clinicId,
        patientId,
        fromDate: from || undefined,
        toDate: to || undefined,
        status: 1,
      };
      const res = await VitalService.getSpecificVitalData(payload);
      setHistoryRows(res?.data || []);
    } catch (e) {
      console.error("Failed to fetch vitals history:", e);
      setHistoryError("Failed to load vitals history");
      setHistoryRows([]);
    } finally {
      setLoadingHistory(false);
    }
  };

  useEffect(() => {
    fetchLatestVitals();
    fetchHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientId]);

  const handleApplyFilter = () => fetchHistory(fromDate, toDate);
  const handleClearFilter = () => {
    setFromDate("");
    setToDate("");
    fetchHistory();
  };

  const handleVitalSuccess = () => {
    fetchLatestVitals();
    fetchHistory(fromDate, toDate);
  };

  const getValue = (code) =>
    latestVitals.find((v) => v.vitalCode === code)?.value ?? null;
  const getStatus = (code) =>
    latestVitals.find((v) => v.vitalCode === code)?.vitalStatus || null;

  const getBpDisplay = () => {
    const sys = getValue("BPS");
    const dia = getValue("BPD");
    if (sys == null && dia == null) return null;
    return `${formatVal(sys)}/${formatVal(dia)}`;
  };
  const getBpStatus = () => getStatus("BPS") || getStatus("BPD");

  const statusBadge = (status) => {
    const s = (status || "NORMAL").toUpperCase();
    const styles =
      s === "NORMAL"
        ? "bg-green-100 text-green-700"
        : s === "CRITICAL"
          ? "bg-red-100 text-red-700"
          : "bg-amber-100 text-amber-700";
    return (
      <span
        className={`rounded-md px-3 py-1 text-[10px] font-semibold ${styles}`}
      >
        {s.charAt(0) + s.slice(1).toLowerCase()}
      </span>
    );
  };

  const historyGrouped = useMemo(
    () => groupHistoryByTimestamp(historyRows),
    [historyRows],
  );

  return (
    <>
      {/* CURRENT VITALS */}
      <div className="rounded-xl border border-gray-200 bg-white shadow-sm">
        <div className="border-b border-gray-200 px-5 py-3 flex items-center justify-between">
          <h2 className="text-sm font-semibold text-[#3b3f97]">
            Current Vitals (Latest)
          </h2>
          <button
            onClick={() => setIsAddVitalModalOpen(true)}
            className="flex items-center gap-1.5 rounded-lg bg-brand-primaryColor px-3 py-1.5 text-xs font-semibold text-white shadow-sm hover:bg-[#4a48b0] transition"
          >
            <FaPlus className="text-[10px]" />
            <span>Add Vitals</span>
          </button>
        </div>

        {loadingLatest ? (
          <div className="flex items-center justify-center py-10 text-sm text-gray-400">
            Loading vitals...
          </div>
        ) : latestVitals.length === 0 ? (
          <div className="flex items-center justify-center py-10 text-sm text-gray-400">
            No vitals recorded yet.
          </div>
        ) : (
          <div className="flex gap-2 overflow-x-auto p-1 m-1">
            {VITAL_CARDS.map((card) => {
              const value = card.isBP ? getBpDisplay() : getValue(card.code);
              const status = card.isBP ? getBpStatus() : getStatus(card.code);
              return (
                <div
                  key={card.code}
                  className="relative flex min-w-[180px] min-h-[180px] flex-col rounded-xl border border-gray-200 bg-white p-3 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-lg"
                >
                  {card.trendKey && (
                    <button
                      onClick={() => setTrendCard(card)}
                      className="absolute right-2 top-2 rounded-full bg-slate-100 p-1.5 transition hover:bg-teal-100"
                    >
                      <IoTrendingUp className="text-sm text-slate-600 hover:text-teal-600" />
                    </button>
                  )}

                  <div className="flex justify-center">
                    <div className="rounded-full bg-slate-100 p-1">
                      {card.icon}
                    </div>
                  </div>

                  <p className="mt-3 text-center text-xs font-semibold text-[#3b3f97]">
                    {card.title}
                  </p>
                  <h3 className="mt-2 text-center text-lg font-bold text-gray-900">
                    {formatVal(value)}
                  </h3>
                  <p className="text-center text-xs text-gray-500">
                    {card.unit}
                  </p>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
        <p className="text-lg font-bold text-[#3b3f97]">Vitals History</p>
        <div className="flex flex-wrap items-center gap-2">
          <input
            type="date"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            className="h-8 rounded-md border border-[#e5e7eb] bg-white px-3 text-[12px] outline-none focus:border-[#5b5bd6]"
          />
          <span className="text-xs text-gray-400">to</span>
          <input
            type="date"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            className="h-8 rounded-md border border-[#e5e7eb] bg-white px-3 text-[12px] outline-none focus:border-[#5b5bd6]"
          />
          <button
            onClick={handleApplyFilter}
            className="h-8 rounded-md bg-brand-primaryColor px-4 text-[12px] font-semibold text-white hover:bg-[#4a48b0]"
          >
            Apply
          </button>
          <button
            onClick={handleClearFilter}
            className="h-8 rounded-md bg-slate-100 border border-[#e5e7eb] px-4 text-[12px] font-semibold text-[#374151]"
          >
            Clear
          </button>
        </div>
      </div>

      <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white mt-3">
        {loadingHistory ? (
          <div className="flex items-center justify-center py-10 text-sm text-gray-400">
            Loading history...
          </div>
        ) : historyError ? (
          <div className="flex items-center justify-center py-10 text-sm text-red-500">
            {historyError}
          </div>
        ) : historyGrouped.length === 0 ? (
          <div className="flex items-center justify-center py-10 text-sm text-gray-400">
            No vitals history found.
          </div>
        ) : (
          <div className="max-h-[300px] overflow-y-auto relative custom-scrollbar">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 sticky top-0 z-10 shadow-sm">
                <tr className="text-left text-xs font-semibold text-[#3b3f97]">
                  <th className="px-4 py-3">Date & Time</th>
                  <th className="px-3 py-3">BP</th>
                  <th className="px-3 py-3">Pulse</th>
                  <th className="px-3 py-3">RR</th>
                  <th className="px-3 py-3">SpO₂</th>
                  <th className="px-3 py-3">Temp</th>
                  <th className="px-3 py-3">Weight</th>
                  <th className="px-3 py-3">Height</th>
                  <th className="px-3 py-3">BMI</th>
                </tr>
              </thead>
              <tbody>
                {historyGrouped.map((row, index) => (
                  <tr
                    key={row.recordedDateTime + index}
                    className="border-t hover:bg-gray-50 transition"
                  >
                    <td className="px-4 py-3 text-xs text-[#4f46e5]">
                      {row.recordedDateTime}
                    </td>
                    <td className="px-3 py-3">
                      {row.BPS || row.BPD
                        ? `${formatVal(row.BPS)}/${formatVal(row.BPD)}`
                        : "--"}
                    </td>
                    <td className="px-3 py-3">{formatVal(row.HR)}</td>
                    <td className="px-3 py-3">{formatVal(row.RR)}</td>
                    <td className="px-3 py-3">{formatVal(row.SPO2)}</td>
                    <td className="px-3 py-3">{formatVal(row.TP)}</td>
                    <td className="px-3 py-3">{formatVal(row.WE)}</td>
                    <td className="px-3 py-3">{formatVal(row.HI)}</td>
                    <td className="px-3 py-3">{formatVal(row.BMI)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* TREND MODAL — reuses Overview's VitalTrendChart */}
      {trendCard && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-5">
          <div className="w-full max-w-3xl rounded-2xl bg-white shadow-2xl">
            <div className="flex items-center justify-between border-b px-6 py-4">
              <div>
                <h2 className="text-xl font-bold text-gray-800">
                  {trendCard.title} Trend
                </h2>
                <p className="text-sm text-gray-500">Vitals over time</p>
              </div>
              <button
                onClick={() => setTrendCard(null)}
                className="bg-slate-100"
              >
                <IoClose className="text-2xl text-gray-500" />
              </button>
            </div>
            <div className="p-6">
              <VitalTrendChart
                patientId={patientId}
                accountId={accountId}
                initialVital={trendCard.trendKey}
              />
            </div>
          </div>
        </div>
      )}

      {/* ADD IPD VITALS MODAL */}
      <AddIPDVitals
        visible={isAddVitalModalOpen}
        onClose={() => setIsAddVitalModalOpen(false)}
        onSuccess={handleVitalSuccess}
        patientId={patientId}
        accountId={accountId}
        clinicId={clinicId}
        admissionId={admissionId}
      />
    </>
  );
}
